import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Box,
    StatusIndicator,
    Button,
    Header,
    Link,
    Pagination,
    SpaceBetween,
    Table,
    TextFilter,
    Alert,
} from "@cloudscape-design/components";
import { getTeamPipelines } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { MonitoredPipeline, MonitoredPipelineResponse } from "src/core/sentinel/types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
export interface SentinelTeamDetailPageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const TeamDetailPage: React.FC<SentinelTeamDetailPageProps> = (
    props: SentinelTeamDetailPageProps
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pipelines, setPipelines] = useState<MonitoredPipeline[]>([]);
    const pageSize = 50;
    const { teamId } = useParams<{ teamId: string }>();
    const [teamName, setTeamName] = useState(location.state?.teamName ?? "");
    useEffect(() => {
        if (!teamId) return;
        const fetchPipelines = async () => {
            try {
                const response = await dispatch(
                    getTeamPipelines({
                        sentinelController: props.sentinelController,
                        teamId: teamId,
                    })
                ).unwrap();
                const pipelineList = (response as any)?.payload?.pipelines;
                setPipelines(pipelineList || []);
                setTeamName(pipelineList?.[0]?.teamName ?? teamName);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setError("Failed to load team pipelines.");
            }
        };
        fetchPipelines();
    }, [teamId]);

    const filteredPipelines = pipelines.filter((p) => {
        const content = [p.pipelineName, p.teamName]
            .join(" ")
            .toLowerCase();
        return !filteringText || content.includes(filteringText.toLowerCase());
    });

    const paginatedItems = filteredPipelines.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );

    const totalPages = Math.ceil(filteredPipelines.length / pageSize);

    useEffect(() => {
        dispatch(setPageTitle("Team Pipelines"));
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    if (!teamId) {
        return (
            <SentinelBasePage>
                {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
                <Box textAlign="center" padding="xl">
                    <StatusIndicator type="error">
                        No team specified. Please navigate from the Teams page.
                    </StatusIndicator>
                </Box>
            </SentinelBasePage>
        );
    }

    return (
        <SentinelBasePage>
            {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
            <Table
                items={paginatedItems}
                columnDefinitions={[
                    {
                        id: "pipelineName",
                        header: "Pipeline Name",
                        cell: (e: MonitoredPipeline) => (
                            <Link href={`#/team/details/${teamId}/pipeline/${encodeURIComponent(e.pipelineName!)}`}>
                                {e.pipelineName}
                            </Link>
                        ),
                        sortingField: "pipelineName",
                        isRowHeader: true,
                    },
                    {
                        id: "packageCount",
                        header: "Packages",
                        cell: (e: MonitoredPipeline) => e.packageCount ?? 0,
                        sortingField: "packageCount",
                    },
                    {
                        id: "teamEmail",
                        header: "Team Email",
                        cell: (e: MonitoredPipeline) => e.teamEmail,
                    },
                ]}
                renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                    `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                }
                loading={loading}
                loadingText="Loading pipelines"
                empty={
                    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                        <SpaceBetween size="m">
                            <b>No pipelines found</b>
                        </SpaceBetween>
                    </Box>
                }
                filter={
                    <TextFilter
                        filteringPlaceholder="Filter pipelines"
                        filteringText={filteringText}
                        onChange={({ detail }) => {
                            setFilteringText(detail.filteringText);
                            setCurrentPageIndex(1);
                        }}
                        countText={`${filteredPipelines.length} matches`}
                    />
                }
                header={
                    <Header
                        actions={
                            <Button variant="link" onClick={() => navigate("/team")}>
                ← Back to Onboarded Teams
                            </Button>
                        }
                    >
            Pipelines for Team: {teamName || teamId}
                    </Header>
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={totalPages}
                        onChange={({ detail }) =>
                            setCurrentPageIndex(detail.currentPageIndex)
                        }
                    />
                }
            />
        </SentinelBasePage>
    );
};
